import { useCallback } from "react";
import useProjectAccess from "rwb/hooks/useProjectAccess";
import useWorkbench from "rwb/pages/project/hooks/useProject";
import ProjectPrivilege from "rwb/pages/project/types/enums/privilege";
import Workbench from "rwb/types/interfaces/project";
import useFundingSources from "~/pages/admin/hooks/useFundingSources";
import { apiMutate } from "~/utils/apiClient";

const { VITE_RWB_API } = import.meta.env;

/**
 * Funding source data and mutation for a project's attached workbench. Reuses
 * the RWB `useProject`/`useProjectAccess` hooks with an explicit workbench id
 * (rather than the `:projectId` route param they normally read) so the
 * nextgen project shell can drive them without any RWB route context.
 * Fetches unconditionally, so callers check cost management first.
 */
export const useProjectFundingSource = (workbenchId: number) => {
  const { data: workbench, mutate } = useWorkbench(workbenchId);
  const { data: fundingSources = [] } = useFundingSources(true, "use");
  // The current source may be one the user can only charge as a guest, so it
  // is named from the unfiltered list.
  const { data: accessibleFundingSources = [] } = useFundingSources(true);
  const canEdit = useProjectAccess(ProjectPrivilege.MANAGE_FUNDING_SOURCE, workbench);

  const updateFundingSource = useCallback(
    async (fundingSourceId: string) => {
      const updated = await apiMutate<Workbench>(`${VITE_RWB_API}/projects/${workbenchId}`, "PUT", {
        funding_source: fundingSourceId,
      });
      mutate(updated, { revalidate: false });
    },
    [workbenchId, mutate]
  );

  return {
    fundingSources,
    currentFundingSourceId: workbench?.funding_source,
    currentFundingSourceName: accessibleFundingSources.find(
      (fundingSource) => String(fundingSource.id) === workbench?.funding_source
    )?.name,
    canEdit,
    updateFundingSource,
  };
};
